import { useDebounce } from '@hooks/useDebounce';
import { useOnClickOutside } from '@hooks/useOnClickOutside';
import { Image } from '@vacasa/react-components-lib';
import React, { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import * as styled from './styled/TextInput.styled';

export interface AutocompleteValue<T> {
    display: string;
    value: T;
}

export interface ComponentProps<T> {
    disabled?: boolean;
    name: string;
    width?: number;
    autocomplete: (value: string, signal?: AbortSignal) => Promise<AutocompleteValue<T>[]>;
    onAutocomplete: (value: AutocompleteValue<T>) => void;
}

export const TextAutocompleteInput: React.FC<ComponentProps<any>> = ({
    disabled = false,
    name,
    width,
    autocomplete,
    onAutocomplete,
}) => {
    const { register, setValue, watch } = useFormContext();
    const watchValue = watch(name);
    const debouncedValue = useDebounce(watchValue, 250);

    useEffect(() => {
        setValue(name, watchValue);
    }, []);
    const methods = register(name);

    const [isLoading, setIsLoading] = useState(false);
    const [open, setOpen] = useState(false);

    const dropdownRef = useRef(null);
    useOnClickOutside(dropdownRef, () => setOpen(false));
    const [suggestions, setSuggestions] = useState<AutocompleteValue<any>[]>([]);

    // Get autcomplete items when input is updated
    useEffect(() => {
        const abortController = new AbortController();
        setIsLoading(true);
        autocomplete(debouncedValue, abortController.signal)
            .then((results) => {
                setSuggestions(results);
                setOpen(results && results.length > 0);
            })
            .finally(() => {
                setIsLoading(false);
            });

        return () => {
            abortController.abort();
        };
    }, [debouncedValue]);

    const handleAutocomplete = (value: AutocompleteValue<any>) => {
        onAutocomplete(value);
        setSuggestions([]);
    };

    return (
        <>
            <styled.TextInput
                $width={width}
                disabled={disabled}
                id={name}
                {...methods}
                value={watchValue || ''}
            />
            {isLoading && (
                <styled.AutocompleteDropdown $width={width} ref={dropdownRef}>
                    <styled.AutocompleteItem>
                        <Image.Spinner width={24} height={24} />
                    </styled.AutocompleteItem>
                </styled.AutocompleteDropdown>
            )}
            {!isLoading && open && (
                <styled.AutocompleteDropdown $width={width} ref={dropdownRef}>
                    {suggestions.map((x, i) => (
                        <styled.AutocompleteItem key={i} onClick={() => handleAutocomplete(x)}>
                            {x.display}
                        </styled.AutocompleteItem>
                    ))}
                </styled.AutocompleteDropdown>
            )}
        </>
    );
};
